import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  generateTableFromSchema,
  Message,
  Modal,
  Section, Skeleton,
  TableSkeleton,
} from '@zolteam/zol-admin-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFullname } from '../../../domain/customers/customersUseCases';
import {
  getExceededPassengerCategories,
  getPassengersTotalCount,
} from '../../../domain/flightBrief/flightBriefUseCases';
import i18n from '../../../i18n';
import { useGetPassengerListQuery } from '../../../services/customerPassengers/customerPassengerQueries';
import { useGetFlightBrief } from '../../../services/flightBriefs/flightBriefsQueries';
import { updateFlightBrief } from '../../../services/flightBriefs/flightBriefsService';
import { getErrorMessage } from '../../../utils/errors/displayError';
import { useGetTerminal } from '../../../services/airports/terminals/adminTerminalQueries';
import { useGetBookingQuery } from '../../../services/bookings/bookingsQueries';
import styles from './FlightBriefDetails.module.css';

const TerminalSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'Terminal',
  properties: {
    type: {
      type: 'string',
      description: ' ',
    },
    terminalId: {
      type: 'string',
      description: i18n.t('flightBrief.fields.terminal.terminalId'),
    },
    name: {
      type: 'string',
      description: i18n.t('flightBrief.fields.terminal.name'),
    },
    address: {
      type: 'string',
      description: i18n.t('flightBrief.fields.terminal.address'),
    },
    phone: {
      type: 'string',
      description: i18n.t('flightBrief.fields.terminal.phone'),
    },
    openingTime: {
      type: 'string',
      description: i18n.t('flightBrief.fields.terminal.openingTime'),
    },
    closingTime: {
      type: 'string',
      description: i18n.t('flightBrief.fields.terminal.closingTime'),
    },
    meetingInfo: {
      type: 'string',
      description: i18n.t('flightBrief.fields.terminal.meetingInfo'),
    },
  },
  additionalProperties: false,
};

const AircraftSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'Aircraft',
  properties: {
    type: {
      type: 'string',
      description: i18n.t('flightBrief.fields.aircraft.type'),
    },
    registration: {
      type: 'string',
      description: i18n.t('flightBrief.fields.aircraft.registration'),
    },
    seatNumber: {
      type: 'string',
      description: i18n.t('flightBrief.fields.aircraft.seatNumber'),
    },
  },
  additionalProperties: false,
};

const PassengerSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'Passenger',
  properties: {
    fullname: {
      type: 'string',
      description: i18n.t('flightBrief.passengers.columns.fullname'),
    },
  },
  additionalProperties: false,
};

/**
 * Flight Brief details component
 * @param {object} props
 * @param {string} props.uuid
 * @param {number} props.customerId
 * @param {number} props.passengerTotal
 * @returns {JSX.Element}
 */
function FlightBriefDetails({ uuid, customerId, passengerTotal }) {
  const [passengerToDelete, setPassengerToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedPassengers, setSelectedPassengers] = useState([]);

  const { t } = useTranslation();
  const {
    data: flightBrief,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetFlightBrief(uuid);

  const {
    data: passengers,
    isLoading: isLoadingPassengers,
  } = useGetPassengerListQuery(customerId);

  // Fetch booking to get airport IDs
  const {
    data: booking,
  } = useGetBookingQuery(
    String(flightBrief?.bookingId),
    { enabled: !!flightBrief?.bookingId },
  );

  const departureAirportId = booking?.departure?.airportId;
  const arrivalAirportId = booking?.arrival?.airportId;

  // Fetch terminal details
  const {
    data: departureTerminal,
    isLoading: isLoadingDepartureTerminal,
  } = useGetTerminal(
    departureAirportId,
    flightBrief?.departureTerminalId,
  );

  const {
    data: arrivalTerminal,
    isLoading: isLoadingArrivalTerminal,
  } = useGetTerminal(
    arrivalAirportId,
    flightBrief?.arrivalTerminalId,
  );

  const putUpdateFlightBriefMutation = useMutation(updateFlightBrief, {
    onSuccess: () => {
      refetch();
    },
  });

  /**
   * Remove a passenger from the flight brief
   * @param {number} passengerId - The passenger ID
   */
  const handleRemovePassengerFromBrief = (passengerId) => {
    const newList = flightBrief?.passengersList
      ?.filter((passenger) => passenger.id !== passengerId)
      .map((passenger) => passenger.id) || [];
    putUpdateFlightBriefMutation.mutate({
      ...flightBrief,
      passengerIdList: newList,
      dutyOfficerId: flightBrief?.dutyOfficer?.id,
    });
  };

  /**
   * @param {CustomerPassenger} passenger
   */
  const handleDeletePassenger = (passenger) => {
    setPassengerToDelete(passenger);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (passengerToDelete) {
      handleRemovePassengerFromBrief(passengerToDelete.id);
    }
    setIsDeleteModalOpen(false);
    setPassengerToDelete(null);
  };

  const handleAddPassenger = () => {
    setSelectedPassengers([]);
    setIsAddModalOpen(true);
  };

  /**
   * Handle the selection of a passenger
   * @param {number} passengerId - The ID of the selected passenger
   */
  const handleSelectPassenger = (passengerId) => {
    setSelectedPassengers((prev) => {
      if (prev.includes(passengerId)) {
        return prev.filter((id) => id !== passengerId);
      }
      return [...prev, passengerId];
    });
  };

  const handleConfirmAdd = () => {
    // Build the complete passenger list with all selected passengers in one go
    const currentPassengerIds = flightBrief?.passengersList?.map(
      (passenger) => passenger.id,
    ) || [];
    const newCompleteList = [...currentPassengerIds, ...selectedPassengers];

    putUpdateFlightBriefMutation.mutate({
      ...flightBrief,
      passengerIdList: newCompleteList,
      dutyOfficerId: flightBrief?.dutyOfficer?.id,
    });

    setIsAddModalOpen(false);
    setSelectedPassengers([]);
  };

  /**
   * Render the content of the add passenger modal
   * @returns {JSX.Element}
   */
  const renderAddModalContent = () => {
    if (isLoadingPassengers) {
      return <Skeleton height="200px" width="100%" shape="rectangle" />;
    }

    if (!passengers?.length) {
      return <p>{t('flightBrief.addModal.noPassengers')}</p>;
    }

    const allowedTotal = getPassengersTotalCount(booking?.passengers, []) || passengerTotal;

    const isPassengerDisabled = (/** @type {boolean} */isChecked) => (isChecked
      ? false : selectedPassengers?.length >= (allowedTotal
        - (flightBrief?.passengersList?.length || 0)));

    const isSelected = (/** @type {number} */id) => selectedPassengers?.includes(id);

    const availablePassengers = passengers
      ?.filter((p) => !flightBrief?.passengersList?.find((bp) => bp.id === p.id));

    const exceededCategories = getExceededPassengerCategories({
      allowedPassengersByCategory: booking?.passengers,
      briefPassengers: flightBrief?.passengersList,
      selectedPassengerIds: selectedPassengers,
      availablePassengers,
    });

    return (
      <div className="margin-top-m d-flex f-column gap-m">
        <div className="d-flex f-column gap-s">
          <div className={`d-flex f-column gap-s overflow-auto ${styles['passenger-modal']}`}>
            {availablePassengers?.map((passenger) => (
              <Checkbox
                key={passenger.id}
                label={`${passenger.fullname} (${t(`flightBrief.passengers.categories.${passenger.category}`)})`}
                checked={isSelected(passenger.id)}
                // @ts-ignore
                onChange={() => handleSelectPassenger(passenger.id)}
                disabled={isPassengerDisabled(isSelected(passenger.id))}
                className={isPassengerDisabled(isSelected(passenger.id)) ? 'opacity-half' : 'opacity-full'}
              />
            ))}
          </div>
          {exceededCategories.length > 0 ? (
            <div>
              <Message>
                {t('flightBrief.addModal.warning', {
                  category: t(`flightBrief.passengers.categories.${exceededCategories[0]}`),
                })}
              </Message>
            </div>
          ) : null}
        </div>
        <div className="d-flex full-width gap-s">
          <Button
            color="noBackground"
            onClick={() => {
              setIsAddModalOpen(false);
              setSelectedPassengers([]);
            }}
          >
            {t('flightBrief.addModal.cancel')}
          </Button>
          <Button
            onClick={handleConfirmAdd}
            disabled={selectedPassengers.length === 0 || exceededCategories.length > 0}
          >
            {t('flightBrief.addModal.confirm')}
          </Button>
        </div>
      </div>
    );
  };

  if (isError) {
    return (
      <div className="margin-bottom-m">
        <Message>
          {getErrorMessage(error
            // @ts-ignore
            .code)}
        </Message>
      </div>
    );
  }

  return (
    <div className="d-flex f-column gap-l">
      {flightBrief?.dutyOfficer?.id ? (
        <p className="p1-default">
          {`${t('flightBrief.fields.dutyOfficer')} : ${getFullname(flightBrief?.dutyOfficer?.firstname, flightBrief?.dutyOfficer?.lastname) || '-'}`}
        </p>
      ) : null}
      {putUpdateFlightBriefMutation?.isError ? (
        <div className="margin-bottom-m">
          <Message>
            {t(`apiErrors.${putUpdateFlightBriefMutation?.error
              // @ts-ignore
              .code}`)}
          </Message>
        </div>
      ) : null}
      <Section id="flight-info" title={t('flightBrief.section.flight')} isDisplay>
        {isLoading ? (
          <Skeleton height="100px" width="100%" shape="rectangle" />
        ) : (
          <div className="d-flex f-column gap-s">
            <p className="p1-default">
              {`${t('flightBrief.fields.flightDuration')} : ${flightBrief?.flightDuration || '-'}`}
            </p>
            <p className="p1-default">
              {`${t('flightBrief.fields.flightNumber')} : ${flightBrief?.flightNumber || '-'}`}
            </p>
          </div>
        )}
      </Section>

      <Section id="aircraft-info" title={t('flightBrief.section.aircraft')} isDisplay>
        {isLoading ? (
          <Skeleton height="100px" width="100%" shape="rectangle" />
        ) : (
          generateTableFromSchema({
            JSONSchema: AircraftSchema,
            data: [{ ...flightBrief?.aircraft }],
          })
        )}
      </Section>

      <Section id="terminals" title={t('flightBrief.section.terminals')} isDisplay>
        {isLoading ? (
          <Skeleton height="200px" width="100%" shape="rectangle" />
        ) : (
          <div>
            {!flightBrief?.departureTerminalId && !flightBrief?.arrivalTerminalId ? (
              <Message>
                {t('flightBrief.terminals.noTerminals')}
              </Message>
            ) : (
              <>
                {!flightBrief?.departureTerminalId && (
                  <Message className="margin-bottom-m">
                    {t('flightBrief.terminals.noDepartureTerminal')}
                  </Message>
                )}
                {!flightBrief?.arrivalTerminalId && (
                  <Message className="margin-bottom-m">
                    {t('flightBrief.terminals.noArrivalTerminal')}
                  </Message>
                )}
                {(isLoadingDepartureTerminal || isLoadingArrivalTerminal) && (
                  <div className="margin-bottom-m">
                    <Skeleton height="100px" width="100%" shape="rectangle" />
                  </div>
                )}
                {generateTableFromSchema({
                  JSONSchema: TerminalSchema,
                  data: [
                    ...(flightBrief?.departureTerminalId && !isLoadingDepartureTerminal ? [{
                      type: t('flightBrief.fields.terminal.departure'),
                      terminalId: departureTerminal?.identityCode,
                      name: departureTerminal?.name,
                      address: departureTerminal?.address,
                      phone: departureTerminal?.phone,
                      openingTime: departureTerminal?.openingTime,
                      closingTime: departureTerminal?.closingTime,
                      meetingInfo: flightBrief?.departureMeetingInfo,
                    }] : []),
                    ...(flightBrief?.arrivalTerminalId && !isLoadingArrivalTerminal ? [{
                      type: t('flightBrief.fields.terminal.arrival'),
                      terminalId: arrivalTerminal?.identityCode,
                      name: arrivalTerminal?.name,
                      address: arrivalTerminal?.address,
                      phone: arrivalTerminal?.phone,
                      openingTime: arrivalTerminal?.openingTime,
                      closingTime: arrivalTerminal?.closingTime,
                      meetingInfo: flightBrief?.arrivalMeetingInfo,
                    }] : []),
                  ],
                })}
              </>
            )}
          </div>
        )}
      </Section>
      <Section id="passengers" title={t('flightBrief.section.passengers')} isDisplay>
        {(flightBrief?.passengersList?.length ?? 0) < (getPassengersTotalCount(booking?.passengers, []) || passengerTotal) ? (
          <div className={`d-flex justify-end ${styles['section-button']}`}>
            <Button
              color="noBackground"
              icon="add"
              size="small"
              onClick={handleAddPassenger}
            >
              {t('customerList.passengers.actions.add')}
            </Button>
          </div>
        ) : null}
        {isLoading ? (
          <TableSkeleton />
        ) : (
          generateTableFromSchema({
            JSONSchema: PassengerSchema,
            data: flightBrief?.passengersList || [],
            labelNoResult: t('flightBrief.passengers.noResult'),
            customColumns: [
              {
                key: 'category',
                description: t('flightBrief.passengers.columns.category'),
                sortable: false,
                position: 'end',
                renderCell: (/** @type {CustomerPassenger} */row) => (
                  <span className="margin-left-s">{t(`flightBrief.passengers.categories.${row.category}`)}</span>
                ),
              },
              {
                key: 'identityFiles',
                description: t('flightBrief.passengers.columns.documents'),
                sortable: false,
                position: 'end',
                renderCell: (/** @type {CustomerPassenger} */row) => (
                  <div className="d-flex f-column gap-xs padding-s">
                    {row.identityFiles?.map((url, index) => (
                      <a className="underline" href={url} target="_blank" rel="noreferrer">
                        {t('flightBrief.passengers.fields.file', { nb: index + 1 })}
                      </a>
                    ))}
                  </div>
                ),
              },
              {
                key: 'actions',
                description: ' ',
                sortable: false,
                position: 'end',
                renderCell: (/** @type {CustomerPassenger} */row) => (
                  <div className="margin-left-m d-flex gap-s">
                    <Button
                      color="noBackground"
                      icon="trash"
                      onClick={() => handleDeletePassenger(row)}
                    // isLoading={isLoadingDeletePassenger && passengerToDelete?.id === row.id}
                    />
                  </div>
                ),
              },
            ],
          })
        )}
      </Section>
      <Section id="additional-info" title={t('flightBrief.section.additionalInfo')} isDisplay>
        {isLoading ? (
          <Skeleton height="100px" width="100%" shape="rectangle" />
        ) : (
          <div className="d-flex f-column gap-s">
            <p className="p1-default">
              {`${t('flightBrief.fields.information')} : ${flightBrief?.information || '-'}`}
            </p>
            <p className="p1-default">
              {`${t('flightBrief.fields.flightRadarLink')} : ${flightBrief?.flightRadarLink || '-'}`}
            </p>
          </div>
        )}
      </Section>
      {isDeleteModalOpen ? (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setPassengerToDelete(null);
          }}
          id="delete-passenger-modal"
          title={t('flightBrief.deleteModal.title')}
          size="small"
        >
          <div className="d-flex f-column gap-m">
            <p>{t('flightBrief.deleteModal.message')}</p>
            <div className="d-flex gap-s justify-end">
              <Button
                color="noBackground"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setPassengerToDelete(null);
                }}
              >
                {t('flightBrief.deleteModal.cancel')}
              </Button>
              <Button onClick={handleConfirmDelete} color="alert">
                {t('flightBrief.deleteModal.confirm')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
      {isAddModalOpen ? (
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
            setSelectedPassengers([]);
          }}
          id="add-passenger-modal"
          title={t('flightBrief.addModal.title')}
          size="medium"
        >
          {renderAddModalContent()}
        </Modal>
      ) : null}
    </div>
  );
}

export default FlightBriefDetails;
